import React from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { animate } from '@/checkout-instore-legacy/utils/animation'

const HORIZONTAL = 'horizontal'
const VERTICAL = 'vertical'
const BOTH = 'both'

const LEFT = 'left'
const RIGHT = 'right'
// TODO: implement vertical swiping
// const TOP = 'top'
// const BOTTOM = 'bottom'
export default class Swipable extends React.Component {
  constructor(props) {
    super(props)

    this.dragContainer = React.createRef()

    this.dragging = false
    this.dragEnabled = false
    this.offset = 0
    this.dragStartPos = {
      x: 0,
      y: 0,
    }
    this.lastDragPos = []
    this.willTrigger = false
  }

  getTouchPosition(event) {
    const { touches } = event
    if (isEmpty(touches)) return null

    const touch = event.touches[0]

    const pos = {
      x: touch.clientX,
      y: touch.clientY,
    }

    return pos
  }

  handleDragStart = (event) => {
    const pos = this.getTouchPosition(event)
    if (pos === null) return

    this.dragging = true
    this.dragLock = false
    this.dragEnabled = false
    this.offset = 0
    this.dragStartPos = {
      ...pos,
    }
    this.lastDragPos = [pos]
    this.willTrigger = false
    this.dispatchTriggerChange(this.willTrigger)
  }

  dispatchTriggerChange = (willTrigger) => {
    const { onTriggerChange } = this.props
    if (onTriggerChange) {
      onTriggerChange(willTrigger)
    }
  }

  handleDragMove = (event) => {
    const {
      onDragStart,
      onLockScroll,
      onSwipeLeft,
      onSwipeRight,
      enabled,
      rubberBanding,
    } = this.props
    if (this.dragging && !this.dragLock) {
      const pos = this.getTouchPosition(event)
      if (pos === null) return

      const threshold = {
        x: 15,
        y: 10,
      }
      const distance = {
        x: pos.x - this.dragStartPos.x,
        y: pos.y - this.dragStartPos.y,
      }

      if (!this.dragEnabled) {
        if (Math.abs(distance.x) >= threshold.x) {
          this.dragEnabled = true
          onDragStart()
          onLockScroll()
        } else if (Math.abs(distance.y) >= threshold.y) {
          this.dragLock = true
        }
      } else {
        this.offset = distance.x
        const limitDragging = (offset) => {
          const rubberBandingMultiplier = 0.3
          return rubberBanding ? offset * rubberBandingMultiplier : 0
        }

        if (!enabled || (!onSwipeLeft && this.offset < 0)) {
          this.offset = limitDragging(this.offset)
        } else if (!enabled || (!onSwipeRight && this.offset > 0)) {
          this.offset = limitDragging(this.offset)
        }

        this.setOffset(this.offset)
        this.lastDragPos.push(pos)

        const willTrigger = this.checkTrigger()
        if (this.willTrigger !== willTrigger) {
          this.dispatchTriggerChange(willTrigger)
        }

        this.willTrigger = willTrigger
      }
    }
  }

  setOffset = (offset) => {
    if (this.dragContainer && this.dragContainer.current) {
      this.props.positionElement(this.dragContainer.current, offset)
      this.props.onUpdateOffset(offset)
    }
  }

  checkTrigger = () => {
    if (!this.props.enabled) {
      return null
    }
    const dragDirection = this.lastDragPos
      .slice(-20)
      .map((cur, i, arr) => {
        const last = arr[i - 1]
        if (last == null) {
          return null
        }
        return cur.x - last.x
      })
      .filter((cur) => cur != null)
      .reduce((sum, cur) => sum + cur, 0)

    const { onSwipeLeft, onSwipeRight } = this.props
    const triggers = {
      [LEFT]:
        onSwipeLeft && dragDirection < 0 && this.offset < -this.props.threshold,
      [RIGHT]:
        onSwipeRight && dragDirection > 0 && this.offset > this.props.threshold,
    }

    if (triggers[LEFT]) {
      return LEFT
    }
    if (triggers[RIGHT]) {
      return RIGHT
    }
    return null
  }

  handleDragEnd = () => {
    if (this.dragging && this.dragEnabled) {
      const willTrigger = this.checkTrigger()
      if (willTrigger) {
        switch (willTrigger) {
          case LEFT:
            this.props.onSwipeLeft()
            break
          case RIGHT:
            this.props.onSwipeRight()
            break
        }
      } else {
        const offsetAnimation = { value: this.offset }
        animate({
          object: offsetAnimation,
          prop: 'value',
          target: 0,
          duration: 0.2,
          onUpdate: (value) => {
            this.setOffset(value)
          },
        })
      }
      this.props.onDragEnd()
      this.props.onUnlockScroll()
      this.dragging = false
    }
  }
  render() {
    const { children, enabled, rubberBanding } = this.props
    return enabled || rubberBanding ? (
      <div
        ref={this.dragContainer}
        onTouchStart={this.handleDragStart}
        onTouchMove={this.handleDragMove}
        onTouchEnd={this.handleDragEnd}
      >
        {children}
      </div>
    ) : (
      children
    )
  }
}

Swipable.propTypes = {
  children: PropTypes.node,
  onSwipeLeft: PropTypes.func,
  onSwipeRight: PropTypes.func,
  onSwipeUp: PropTypes.func,
  onSwipeDown: PropTypes.func,
  onTriggerChange: PropTypes.func,
  onLockScroll: PropTypes.func,
  onUnlockScroll: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  threshold: PropTypes.number,
  positionElement: PropTypes.func,
  onUpdateOffset: PropTypes.func,
  enabled: PropTypes.bool,
  direction: PropTypes.oneOf([HORIZONTAL, VERTICAL, BOTH]),
  rubberBanding: PropTypes.bool,
}

Swipable.defaultProps = {
  onSwipeLeft: null,
  onSwipeRight: null,
  onSwipeUp: null,
  onSwipeDown: null,
  onTriggerChange: null,
  onLockScroll: () => {},
  onUnlockScroll: () => {},
  onDragStart: () => {},
  onDragEnd: () => {},
  positionElement: (element, offset, vertical) => {
    if (vertical) {
      element.style.transform = `translate3d(0,${offset}px,0)`
    } else {
      element.style.transform = `translate3d(${offset}px,0,0)`
    }
  },
  onUpdateOffset: () => {},
  threshold: 0,
  enabled: true,
  direction: HORIZONTAL,
  rubberBanding: false,
}
